/**
 * A Hand's trigger, in a sentence.
 *
 * The roster was printing the declaration verbatim: `30 6 * * * Asia/Dubai`
 * and `on nb_kyc_cases`. Both are true and neither is readable — the second
 * also puts an internal table name on a client's screen.
 */
export type Trigger =
  | { kind: "cron"; cron: string; tz: string }
  | { kind: "event"; table: string; where?: string; poll_s?: number }
  | { kind: string; [k: string]: unknown };

import { cronInWords } from "@workforceos/shared";

/** "Campaign asset" → "campaign asset"; "KYC case" keeps its capitals. */
const inSentence = (w: string) => (/^[A-Z]{2}/.test(w) ? w : w.charAt(0).toLowerCase() + w.slice(1));

/** "Asia/Dubai" → "Dubai time". */
const zoneWords = (tz: string) => `${(tz.split("/").pop() ?? tz).replace(/_/g, " ")} time`;

/**
 * @param recordLabel what the Hand calls the thing it acts on ("Campaign
 * asset"), so an event trigger can be described without naming a table or
 * the condition it polls for.
 */
export function scheduleLabel(
  trigger: Trigger | null,
  recordLabel?: string | null,
): string {
  if (!trigger) return "No trigger declared";
  if (trigger.kind === "cron" && typeof trigger["cron"] === "string") {
    const tz = typeof trigger["tz"] === "string" ? trigger["tz"] : "";
    const when = inSentence(cronInWords(trigger["cron"] as string, "en"));
    return tz ? `Runs ${when}, ${zoneWords(tz)}` : `Runs ${when}`;
  }
  if (trigger.kind === "event") {
    const subject = recordLabel ? inSentence(recordLabel) : "record";
    return `Runs when a ${subject} is submitted`;
  }
  if (trigger.kind === "on_request") return "Runs when someone asks";
  return "Runs on demand";
}

/** `sales_analyst` → "Sales analyst". The role a Hand holds, not a membership. */
export function handRoleLabel(roleKey: string | null): string | null {
  if (!roleKey) return null;
  const words = roleKey.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
